import { A } from '@mobily/ts-belt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'common/hooks/useTranslation';
import { Lambda } from 'common/types';
import { FC } from 'react';
import { PreviewItem } from '../types/PreviewItem';

type Props = {
  label: string;
  items: ReadonlyArray<PreviewItem>;
  onItemClick: Lambda<PreviewItem, void>;
};

/**
 * Component to display photo thumbnails
 */
const Photos: FC<{ items: ReadonlyArray<string>; label: string }> = ({ items, label }) => {
  // Filter out blob URLs from server items (they shouldn't be there, but just in case)

  // Combine server URLs with local photos
  const amountOfPhotos = items.length;

  if (amountOfPhotos === 0) {
    return null;
  }

  const maxFirstThreePhotosUrls = A.slice(items, 0, 3);

  return (
    <List sx={{ display: 'flex', gap: 0.25, p: 0 }}>
      {maxFirstThreePhotosUrls.map((photoUrl, index) => (
        <ListItem key={`${label}-${photoUrl}}`} sx={{ p: 0 }}>
          <Box component="img" src={photoUrl} alt={`${label}-image-${index}`} width={32} height={32} />
        </ListItem>
      ))}
    </List>
  );
};

/**
 * Wrapper component for a single accordion item that can use hooks
 */
const AccordionItem: FC<{
  item: PreviewItem;
  index: number;
  label: string;
  onItemClick: Lambda<PreviewItem, void>;
}> = ({ item, index, label, onItemClick }) => {
  return (
    <Box key={item.id}>
      <ListItem
        sx={{ cursor: 'pointer' }}
        onClick={() => {
          onItemClick(item);
        }}
      >
        <Stack sx={{ width: '100%' }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography
              color="primary"
              sx={{ flex: '1 1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {item.text ?? `${item.id.split('-')[0]} - ${index}`}
            </Typography>
            <Photos items={item.photosUrls} label={label} />
          </Grid>
          <Typography>{item.location}</Typography>
        </Stack>
      </ListItem>
      <Divider />
    </Box>
  );
};

export const ObjectAccordion: FC<Props> = ({ label, items, onItemClick }) => {
  const amountOfItems = items.length;
  const { t } = useTranslation();

  if (amountOfItems === 0) {
    return (
      <Typography>
        {label} {t('areas.object.unavailable')}
      </Typography>
    );
  }

  return (
    <Accordion TransitionProps={{ unmountOnExit: true }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Grid container gap={1}>
          <Typography>{label}</Typography>
          <Typography color="primary">({amountOfItems})</Typography>
        </Grid>
      </AccordionSummary>
      <AccordionDetails>
        <List>
          {items.map((item, index) => (
            <AccordionItem key={item.id} item={item} index={index} label={label} onItemClick={onItemClick} />
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
